/**
 * NOVA CHARTER — keyboard input source.
 *
 * Four *independent* key schemes share one keyboard, so four people on a couch
 * with no controllers can still play — including the simultaneous sealed-bid
 * phases, where all four must be able to hold keys at the same time.
 *
 * Everything keys off `KeyboardEvent.code` (physical key position) rather than
 * `.key`, which means AZERTY/QWERTZ/Dvorak players get the same physical layout
 * as everyone else, and the numpad scheme keeps working with NumLock off (where
 * `.key` for Numpad8 becomes "ArrowUp" but `.code` stays "Numpad8").
 *
 * Auto-repeat is driven from `poll(nowMs)` — the browser's own repeat is
 * ignored, because its initial delay and rate come from OS settings and would
 * make the keyboard cursor feel different from a gamepad cursor.
 *
 * Like `gamepad.ts` this module knows nothing about seats; slot numbers here are
 * scheme indices 0..3 and `inputRouter.ts` maps them onto `PlayerId`s.
 */

import type { InputButton, InputEvent } from '../game/types';
import { REPEAT_DELAY_MS, REPEAT_INTERVAL_MS } from '../game/constants';

type Edge = InputEvent['edge'];

/* ------------------------------------------------------------------- public */

export interface KeyScheme {
  readonly scheme: number;
  /** Human-readable, e.g. `"Keyboard (WASD)"`. */
  readonly label: string;
  /** `KeyboardEvent.code` → logical button. */
  readonly keys: Readonly<Record<string, InputButton>>;
}

/** Same positional-argument shape as `GamepadEmit`, with `scheme` for `index`. */
export type KeyboardEmit = (
  scheme: number,
  button: InputButton,
  edge: Edge,
  value: number,
) => void;

export interface KeyboardSourceOptions {
  onEvent: KeyboardEmit;
  /**
   * Called when a scheme-specific key is pressed, so the router can follow the
   * "who is typing" cursor for hotseat play.
   */
  onSchemeActive?: (scheme: number) => void;
}

/**
 * The four schemes. Chosen so that no two overlap and each cluster is reachable
 * by one pair of hands: left of the board, right of the board, centre, numpad.
 */
export const KEY_SCHEMES: readonly KeyScheme[] = [
  {
    scheme: 0,
    label: 'Keyboard (WASD)',
    keys: {
      KeyW: 'up',
      KeyS: 'down',
      KeyA: 'left',
      KeyD: 'right',
      KeyF: 'a',
      KeyG: 'b',
      KeyR: 'x',
      KeyT: 'y',
      Digit1: 'lb',
      Digit2: 'rb',
      Digit3: 'lt',
      Digit4: 'rt',
    },
  },
  {
    scheme: 1,
    label: 'Keyboard (Arrows)',
    keys: {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
      Period: 'a',
      Comma: 'b',
      Slash: 'x',
      Semicolon: 'y',
      BracketLeft: 'lb',
      BracketRight: 'rb',
      Quote: 'lt',
      Backslash: 'rt',
    },
  },
  {
    scheme: 2,
    label: 'Keyboard (IJKL)',
    keys: {
      KeyI: 'up',
      KeyK: 'down',
      KeyJ: 'left',
      KeyL: 'right',
      KeyH: 'a',
      KeyN: 'b',
      KeyY: 'x',
      KeyU: 'y',
      Digit6: 'lb',
      Digit7: 'rb',
      Digit8: 'lt',
      Digit9: 'rt',
    },
  },
  {
    scheme: 3,
    label: 'Keyboard (Numpad)',
    keys: {
      Numpad8: 'up',
      Numpad5: 'down',
      Numpad4: 'left',
      Numpad6: 'right',
      Numpad0: 'a',
      NumpadDecimal: 'b',
      Numpad7: 'x',
      Numpad9: 'y',
      NumpadAdd: 'lb',
      NumpadSubtract: 'rb',
      NumpadMultiply: 'lt',
      NumpadDivide: 'rt',
    },
  },
];

/**
 * Keys that belong to no scheme in particular. They are attributed to the
 * *active* scheme — the last one to press one of its own keys — which is what
 * makes single-keyboard hotseat play work.
 */
export const GLOBAL_KEYS: Readonly<Record<string, InputButton>> = {
  Escape: 'back',
  Enter: 'start',
  Space: 'a',
};

export const SCHEME_COUNT = KEY_SCHEMES.length;

export function keyboardSchemeLabel(scheme: number): string {
  const s = KEY_SCHEMES[scheme];
  return s ? s.label : `Keyboard ${scheme + 1}`;
}

/* ------------------------------------------------------------------ tables */

/**
 * Fixed slot layout: `scheme * BUTTONS_PER_SCHEME + BUTTON_INDEX[button]`.
 * Fixed-size arrays mean `poll()` allocates nothing and needs no iterators.
 */
const BUTTON_ORDER: readonly InputButton[] = [
  'a', 'b', 'x', 'y', 'lb', 'rb', 'lt', 'rt', 'start', 'back', 'up', 'down', 'left', 'right',
];
const BUTTONS_PER_SCHEME = BUTTON_ORDER.length;
/** Indices into BUTTON_ORDER of the four directions, in up/down/left/right order. */
const DIR_SLOTS: readonly number[] = [10, 11, 12, 13];

const BUTTON_INDEX: Record<string, number> = (() => {
  const m: Record<string, number> = {};
  for (let i = 0; i < BUTTON_ORDER.length; i++) m[BUTTON_ORDER[i]] = i;
  return m;
})();

interface KeyBinding {
  readonly scheme: number;
  readonly button: InputButton;
}

/** `code` → binding, for the four per-player schemes. */
const CODE_BINDINGS: Map<string, KeyBinding> = (() => {
  const m = new Map<string, KeyBinding>();
  for (let i = 0; i < KEY_SCHEMES.length; i++) {
    const scheme = KEY_SCHEMES[i];
    for (const code in scheme.keys) {
      m.set(code, { scheme: scheme.scheme, button: scheme.keys[code] });
    }
  }
  return m;
})();

/** `code` → button, for the scheme-agnostic keys. */
const GLOBAL_BINDINGS: Map<string, InputButton> = (() => {
  const m = new Map<string, InputButton>();
  for (const code in GLOBAL_KEYS) m.set(code, GLOBAL_KEYS[code]);
  return m;
})();

/* ---------------------------------------------------------------- helpers */

/**
 * Never steal keys from a text field — the lobby has a rename box, and the
 * scoreboard screen may have one too.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!target) return false;
  if (typeof HTMLElement === 'undefined' || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

/**
 * Only swallow the browser default for keys the game actually consumes, and
 * never for a browser/OS combination:
 *  - Ctrl/Cmd/Alt combos stay alive (Cmd+R, Ctrl+Shift+I, Alt+Tab…).
 *  - Function keys stay alive (F5 reload, F11 fullscreen, F12 devtools).
 * What is left is worth preventing: Space and the arrows scroll the page,
 * Enter re-activates the last focused button, `/` opens quick-find in Firefox,
 * and Numpad keys can trigger caret browsing.
 */
function shouldPreventDefault(e: KeyboardEvent, consumed: boolean): boolean {
  if (!consumed) return false;
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (/^F\d{1,2}$/.test(e.code)) return false;
  return true;
}

/* ----------------------------------------------------------------- source */

export class KeyboardSource {
  private readonly opts: KeyboardSourceOptions;

  /**
   * Held physical keys → the slot they were attributed to. Presence here is
   * how the browser's own repeated keydowns are ignored, and how blur knows
   * what to release.
   */
  private readonly heldCodes = new Map<string, number>();

  /**
   * Reference count per logical slot. Two physical keys can map to one logical
   * button (Space and the scheme's own `a` key), and the game must see exactly
   * one `down` and one `up`.
   */
  private readonly slotCount: number[];
  /**
   * Next repeat deadline per slot, or -1 for "held, deadline not seeded yet".
   * Only directional slots are used. The deadline is always seeded inside
   * `poll()` so that the *only* clock involved is the one the router passes in
   * — mixing `performance.now()` here with a `Date.now()` caller there would
   * make every repeat fire instantly.
   */
  private readonly slotNext: number[];

  /** Scheme that global keys (Escape/Enter/Space) are attributed to. */
  private activeScheme = 0;
  private running = false;

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    this.handleKeyDown(e);
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.handleKeyUp(e);
  };

  /**
   * Alt-tab / window-switch / OS overlay all steal focus *without* delivering
   * keyup, which would otherwise leave the cursor sliding forever.
   */
  private readonly onBlur = (): void => {
    this.releaseAll();
  };

  private readonly onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.hidden) this.releaseAll();
  };

  constructor(opts: KeyboardSourceOptions) {
    this.opts = opts;
    const slots = SCHEME_COUNT * BUTTONS_PER_SCHEME;
    this.slotCount = new Array<number>(slots).fill(0);
    this.slotNext = new Array<number>(slots).fill(0);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    if (typeof window !== 'undefined') {
      // Non-passive (the default for key events) so preventDefault() works.
      window.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('keyup', this.onKeyUp);
      window.addEventListener('blur', this.onBlur);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('keyup', this.onKeyUp);
      window.removeEventListener('blur', this.onBlur);
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
    this.releaseAll();
  }

  dispose(): void {
    this.stop();
  }

  /**
   * Which scheme owns the global keys. The router sets this from the game's
   * active player so that Space/Enter/Escape follow the hotseat baton.
   */
  setActiveScheme(scheme: number): void {
    if (scheme >= 0 && scheme < SCHEME_COUNT) this.activeScheme = scheme;
  }

  get currentScheme(): number {
    return this.activeScheme;
  }

  /**
   * Directional auto-repeat, driven by the router so the cadence is identical
   * to the gamepad's (both read REPEAT_DELAY_MS / REPEAT_INTERVAL_MS, both emit
   * at most one repeat per poll and rebase on `now` so a backgrounded tab does
   * not fire a burst on return).
   */
  poll(nowMs: number): void {
    for (let scheme = 0; scheme < SCHEME_COUNT; scheme++) {
      const base = scheme * BUTTONS_PER_SCHEME;
      for (let d = 0; d < DIR_SLOTS.length; d++) {
        const idx = base + DIR_SLOTS[d];
        if (this.slotCount[idx] <= 0) continue;
        if (this.slotNext[idx] < 0) {
          this.slotNext[idx] = nowMs + REPEAT_DELAY_MS;
          continue;
        }
        if (nowMs < this.slotNext[idx]) continue;
        this.slotNext[idx] = nowMs + REPEAT_INTERVAL_MS;
        this.opts.onEvent(scheme, BUTTON_ORDER[DIR_SLOTS[d]], 'repeat', 1);
      }
    }
  }

  /** Synthetic analog state, so keyboard seats can drive the same camera code. */
  writeAxes(
    scheme: number,
    out: { lx: number; ly: number; rx: number; ry: number; lt: number; rt: number },
  ): boolean {
    out.rx = 0;
    out.ry = 0;
    if (scheme < 0 || scheme >= SCHEME_COUNT) {
      out.lx = 0;
      out.ly = 0;
      out.lt = 0;
      out.rt = 0;
      return false;
    }
    const base = scheme * BUTTONS_PER_SCHEME;
    const up = this.slotCount[base + BUTTON_INDEX.up] > 0 ? 1 : 0;
    const down = this.slotCount[base + BUTTON_INDEX.down] > 0 ? 1 : 0;
    const left = this.slotCount[base + BUTTON_INDEX.left] > 0 ? 1 : 0;
    const right = this.slotCount[base + BUTTON_INDEX.right] > 0 ? 1 : 0;
    out.lx = right - left;
    // Screen convention, matching a real stick: +y is down.
    out.ly = down - up;
    out.lt = this.slotCount[base + BUTTON_INDEX.lt] > 0 ? 1 : 0;
    out.rt = this.slotCount[base + BUTTON_INDEX.rt] > 0 ? 1 : 0;
    return true;
  }

  /** Emit `up` for every held key and forget them all. */
  releaseAll(): void {
    if (this.heldCodes.size === 0) return;
    // Snapshot first: the emit callback may re-enter (e.g. the UI tears down a
    // screen), and mutating the map while iterating it would be undefined.
    const codes: string[] = [];
    this.heldCodes.forEach((_slot, code) => codes.push(code));
    for (let i = 0; i < codes.length; i++) this.releaseCode(codes[i]);
  }

  /* ---------------------------------------------------------- internals */

  private handleKeyDown(e: KeyboardEvent): void {
    if (isTextEntryTarget(e.target)) return;

    const code = e.code;
    const scheme = this.resolveScheme(code);
    const button = resolveButton(code);

    if (shouldPreventDefault(e, button !== null)) e.preventDefault();
    if (button === null) return;

    // Ignore the browser's own repeat entirely (`e.repeat` and duplicate
    // keydowns): repeat cadence is ours, from poll().
    if (this.heldCodes.has(code)) return;

    // A scheme's own key moves the hotseat baton; global keys never do, or
    // pressing Space would hand Escape/Enter to whoever pressed it last.
    if (CODE_BINDINGS.has(code)) {
      this.activeScheme = scheme;
      this.opts.onSchemeActive?.(scheme);
    }

    const idx = scheme * BUTTONS_PER_SCHEME + BUTTON_INDEX[button];
    this.heldCodes.set(code, idx);
    const wasHeld = this.slotCount[idx] > 0;
    this.slotCount[idx]++;
    if (wasHeld) return; // another key already holds this logical button

    // -1 = "seed my repeat deadline on the next poll" (see slotNext).
    this.slotNext[idx] = isDirectionalSlot(idx) ? -1 : 0;
    this.opts.onEvent(scheme, button, 'down', 1);
  }

  private handleKeyUp(e: KeyboardEvent): void {
    // No text-entry guard here: if a key went down before focus moved into a
    // text field, its keyup must still release it.
    const code = e.code;
    if (!this.heldCodes.has(code)) return;
    if (shouldPreventDefault(e, true)) e.preventDefault();
    this.releaseCode(code);
  }

  /** Release one physical key, emitting `up` only when its slot hits zero. */
  private releaseCode(code: string): void {
    const idx = this.heldCodes.get(code);
    this.heldCodes.delete(code);
    if (idx === undefined) return;
    if (this.slotCount[idx] > 0) this.slotCount[idx]--;
    if (this.slotCount[idx] > 0) return;
    this.slotNext[idx] = 0;
    const scheme = Math.floor(idx / BUTTONS_PER_SCHEME);
    const button = BUTTON_ORDER[idx % BUTTONS_PER_SCHEME];
    this.opts.onEvent(scheme, button, 'up', 0);
  }

  /** Scheme-specific keys own their scheme; global keys borrow the active one. */
  private resolveScheme(code: string): number {
    const bound = CODE_BINDINGS.get(code);
    return bound ? bound.scheme : this.activeScheme;
  }
}

/** `null` = the game does not consume this key, so leave it to the browser. */
function resolveButton(code: string): InputButton | null {
  const bound = CODE_BINDINGS.get(code);
  if (bound) return bound.button;
  const global = GLOBAL_BINDINGS.get(code);
  return global ?? null;
}

function isDirectionalSlot(idx: number): boolean {
  const within = idx % BUTTONS_PER_SCHEME;
  for (let i = 0; i < DIR_SLOTS.length; i++) if (DIR_SLOTS[i] === within) return true;
  return false;
}
